/**
 * WuaPlusReport — the paid $49 report reader at
 * `/wua-plus/report/:id`. Owner-only; the backend returns 403 for
 * anyone else. Renders the stored Markdown with a light Prose style
 * and a Print / Download button so the buyer can archive the PDF.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ArrowLeft, FileDown, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import ReactMarkdown from "react-markdown";
import PathwaySEO from "@/components/app/PathwaySEO";

/**
 * Trigger the browser print dialog. We tweak `document.title` first so
 * the default "Save as PDF" filename becomes something legible like
 * `WUA Personalised Report — 2026-02-05.pdf` instead of the site title.
 * Restored on afterprint so navigation isn't polluted.
 */
function saveAsPDF(generatedAt) {
  const iso = (generatedAt || new Date().toISOString()).slice(0, 10);
  const orig = document.title;
  document.title = `WUA Personalised Report — ${iso}`;
  const restore = () => {
    document.title = orig;
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  window.print();
}

export default function WuaPlusReport() {
  const { id } = useParams();
  const [rpt, setRpt] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setRpt(null); setErr(null);
    api.get(`/wua-plus/report/${encodeURIComponent(id)}`)
      .then((r) => !cancelled && setRpt(r.data))
      .catch((e) => !cancelled && setErr(
        e?.response?.status === 403
          ? "This report belongs to another account. Please sign in with the account that made the purchase."
          : e?.response?.status === 404
            ? "Report not found."
            : "Could not load the report."
      ));
    return () => { cancelled = true; };
  }, [id]);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <p className="text-stone-600" data-testid="wua-plus-report-error">{err}</p>
        <Link to="/plus" className="mt-4 inline-block text-[#4C1D95] underline">← Back to WUA</Link>
      </div>
    );
  }
  if (!rpt) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-[#4C1D95]" />
      </div>
    );
  }

  return (
    <div data-testid="wua-plus-report-page" className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
      <PathwaySEO
        title="Your WUA Personalised Report"
        description="A personalised university strategy report from WhichUniversityAdvisor — the $49 pre-WUA+ product. Standalone service; upgrade to WUA+ any time for a 1:1 advisor call."
        path={`/wua-plus/report/${id}`}
      />
      <div className="flex items-center justify-between print:hidden">
        <Link
          to="/plus"
          className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
          data-testid="wua-plus-report-back"
        >
          <ArrowLeft className="h-3 w-3" /> Back to my report page
        </Link>
        <Button
          data-testid="wua-plus-report-print"
          variant="outline"
          onClick={() => saveAsPDF(rpt.generated_at)}
          size="sm"
          className="rounded-md"
        >
          <FileDown className="h-4 w-4 mr-1.5" /> Save as PDF
        </Button>
      </div>

      {/* Print-only header — hidden on screen (`hidden print:block`).
          Gives the printed page a clean brand mark + date so a family
          reading the physical/PDF copy still sees "who this is from"
          without the on-screen chrome. */}
      <div className="hidden print:block mb-6 pb-4 border-b border-stone-300">
        <div className="text-[10px] font-underground-sc tracking-[0.2em] text-stone-500">
          WhichUniversityAdvisor · Personalised Report
        </div>
        <div className="text-[11px] font-mono text-stone-500 mt-1">
          Generated {new Date(rpt.generated_at).toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
          })} · Report ID {rpt.report_id.slice(0, 8)}
        </div>
      </div>

      <div className="mt-6 flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider text-emerald-800 print:hidden">
        <ShieldCheck className="h-3 w-3" /> Paid · $49 · Yours forever
      </div>

      <article
        data-testid="wua-plus-report-body"
        className="mt-4 prose prose-stone max-w-none prose-headings:font-editorial prose-headings:text-[#002147] prose-h1:text-4xl prose-h2:text-2xl prose-a:text-[#4C1D95]"
      >
        <ReactMarkdown>{rpt.markdown}</ReactMarkdown>
      </article>

      <div
        data-testid="wua-plus-report-upgrade-strip"
        className="mt-10 rounded-xl border border-[#4C1D95]/25 bg-[#F5F0FF] p-5 print:hidden"
      >
        <div className="text-[10px] font-underground-sc tracking-[0.18em] text-[#4C1D95]">
          Optional next step
        </div>
        <h3 className="mt-1 font-editorial text-xl text-[#002147]">
          Want a human to walk through the nuance with you?
        </h3>
        <p className="mt-2 text-sm text-stone-700 leading-relaxed">
          This report helps to advise <em>what</em> to do. A{" "}
          <Link to="/wua-plus" className="text-[#4C1D95] underline font-medium">
            WUA+ professional advisor
          </Link>{" "}
          session helps you decide the <em>what</em> AND <em>how</em> —
          trade-offs between two shortlisted courses, scholarship
          conversation scripts, or a second opinion on financial
          exposure. Standalone premium service — book only if it's
          useful after reading your report.
        </p>
      </div>

      <div className="mt-8 pt-6 border-t border-stone-200 text-[11px] font-mono text-stone-500 flex items-center justify-between print:hidden">
        <div>
          Generated {new Date(rpt.generated_at).toLocaleString()} ·{" "}
          <Link to="/methodology" className="underline text-[#4C1D95]">Methodology</Link>
        </div>
        <div>
          Report ID: <span className="text-stone-400">{rpt.report_id.slice(0, 8)}</span>
        </div>
      </div>
    </div>
  );
}
